import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { analyticsStore, fetchDevices } from "../stores/analytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BarChart, PieChart } from "../components/charts";
import { AlertCircle } from "lucide-react";
import { formatNumber } from "@/lib/utils";

export default function AnalyticsDevices() {
  const { devices, timeRange } = useSnapshot(analyticsStore);
  const { data, loading, error } = devices;

  useEffect(() => {
    fetchDevices();
  }, [timeRange]);

  // Transform data for charts
  const platformData =
    data?.by_platform.map((p) => ({
      name: p.platform,
      value: p.count,
    })) ?? [];

  const osData =
    data?.by_os_version.slice(0, 5).map((os) => ({
      name: os.os_version,
      value: os.count,
    })) ?? [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2 mb-6">
        {/* Platform Distribution */}
        {loading ? (
          <Skeleton className="h-[350px]" />
        ) : (
          <PieChart
            data={platformData}
            title="Platform Distribution"
            height={300}
          />
        )}

        {/* OS Versions */}
        {loading ? (
          <Skeleton className="h-[350px]" />
        ) : (
          <BarChart
            data={osData}
            title="Top OS Versions"
            dataKey="value"
            xAxisKey="name"
            color="#667eea"
            height={300}
          />
        )}
      </div>

      {/* Device Models Table */}
      <Card>
        <CardHeader>
          <CardTitle>Device Models</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.by_model.map((device) => {
                  const total = data.by_model.reduce(
                    (sum, d) => sum + d.count,
                    0,
                  );
                  const percentage =
                    total > 0 ? ((device.count / total) * 100).toFixed(1) : "0";
                  return (
                    <TableRow key={device.model}>
                      <TableCell className="font-medium">
                        {device.model}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(device.count)}
                      </TableCell>
                      <TableCell className="text-right">
                        {percentage}%
                      </TableCell>
                    </TableRow>
                  );
                }) ?? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
